import { ChefHat } from 'lucide-react';
import { cn } from '~/common/utils/styleUtil';
import { useSalesordersetFilter } from '~/features/catalog/routes/resources/SalesOrderSetFilterRoute';
import { Checkbox } from '~/common/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import { useUiContext } from '~/common/components/context/useUiContext.ts';
import { useNavigation } from 'react-router';
import { useBasePath } from '~/common/hooks/useBasePath.ts';
import { Media } from '~/media.ts';
import { useEffect, useState, useRef } from 'react';

export function FilterOverlay({
  isVisible,
  alignment = 'center',
  fadeInDelay = 3000,
  fadeOutDelay = 4000
}: Readonly<{
  isVisible: boolean;
  alignment?: 'center' | 'right';
  fadeInDelay?: number;
  fadeOutDelay?: number;
}>) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const hasBeenShownRef = useRef(false);

  useEffect(() => {
    if (isVisible && !hasBeenShownRef.current) {
      hasBeenShownRef.current = true;
      setShouldRender(true);

      const delayTimer = setTimeout(() => {
        setShow(true);
      }, fadeInDelay);

      const hideTimer = setTimeout(() => {
        setShow(false);
        setTimeout(() => setShouldRender(false), 2000); // 500ms = transition duration
      }, fadeInDelay + fadeOutDelay);

      return () => {
        clearTimeout(delayTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isVisible, fadeInDelay, fadeOutDelay]);

  if (!shouldRender) return null;

  const alignmentClasses = {
    center: 'left-1/2 transform -translate-x-1/2',
    right: 'right-0'
  };

  const arrowClasses = {
    center: 'left-1/2 transform -translate-x-1/2',
    right: 'right-1.5'
  };

  return (
    <div
      className={`absolute w-[300px] top-full mt-1 z-10 bg-primary text-white text-md px-6 py-4 rounded-md shadow-lg border transition-opacity duration-2000 ${alignmentClasses[alignment]} ${
        show ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className={`absolute -top-1 w-2 h-2 bg-primary rotate-45 ${arrowClasses[alignment]}`}></div>
      <div className={'mb-2 font-bold text-lg'}>{t('Gekaufte Artikel Filter ist aktiv')}</div>
      <div>{t('Es werden nur Artikel angezeigt, welche Sie bereits gekauft haben')}</div>
      <div className={'flex justify-end mt-8'}>
        <button type="button" className={'hover:font-fold'} onClick={() => setShow(false)}>
          {t('Meldung ausblenden')}
        </button>
      </div>
    </div>
  );
}

export function QuickSearchFilter() {
  const navigation = useNavigation();
  const { path } = useBasePath();
  const {
    isFilterAvailable,
    isActive,
    setSalesOrdersetFilter,
    isLoading: isSalesOrderSetLoading
  } = useSalesordersetFilter();
  const { isSearchFilterInProgress, setIsSearchFilterInProgress } = useUiContext();
  const isLoading = navigation.state === 'loading' && navigation.location.pathname === `${path}/catalog`;
  const isSearchFilterDisabled = isSearchFilterInProgress || isLoading || isSalesOrderSetLoading;
  const handleSalesOrderSetFilterChange = (checked: boolean) => {
    if (!isSearchFilterDisabled) {
      setIsSearchFilterInProgress(true);
      setSalesOrdersetFilter(checked);
    }
  };

  if (!isFilterAvailable) {
    return null;
  }

  return (
    <SalesOrdersetCheckBox
      isLoading={isSearchFilterDisabled}
      checked={isActive}
      onCheckedChange={handleSalesOrderSetFilterChange}
    />
  );
}

function SalesOrdersetCheckBox({
  isLoading,
  checked,
  onCheckedChange
}: Readonly<{
  isLoading: boolean;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}>) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex items-center space-x-2 px-2')}>
      <Media lessThan={'desktop'} className={'flex'}>
        <div className="relative">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              disabled={isLoading}
              checked={checked}
              onChange={e => onCheckedChange(e.target.checked)}
              data-testid={'quicksearchfilter-salesorderset'}
              className="sr-only"
            />
            <span className="relative flex items-center justify-center">
              <ChefHat
                fill={checked ? 'var(--color-primary-light)' : 'white'}
                size={24}
                className={cn(checked ? 'text-primary' : 'text-base', isLoading && 'opacity-30')}
                aria-hidden="true"
              />
            </span>
          </label>
          {checked && <FilterOverlay isVisible={checked} alignment={'right'} />}
        </div>
      </Media>
      <Media greaterThanOrEqual={'desktop'} className={'flex items-center space-x-2'}>
        <div className="relative m-0 p-0 flex mr-2">
          <Checkbox
            id="quickSalesOrderSetFilter"
            checked={checked}
            disabled={isLoading}
            onCheckedChange={onCheckedChange}
            data-testid={'quicksearchfilter-salesorderset'}
            name={'salesOrderSet'}
          />
          {checked && <FilterOverlay isVisible={checked} />}
        </div>
        <label
          htmlFor="quickSalesOrderSetFilter"
          className="text-sm font-medium text-nowrap leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {t('Gekaufte Artikel')}
        </label>
      </Media>
    </div>
  );
}
